import { customComponents } from "hec/builder";
import { useMemo } from "react";

import { type BuilderContent, Content } from "@builder.io/sdk-react";

import { GuardedBuilderLink } from "~/hec/builder/guarded-builder-link";
import { useBuilder } from "~/hec/hooks/useBuilder";

import { getPreviewBlocks } from "./utils";

/**
 * SFCC-11505 — port of composable `lessonContentPreview.jsx`. Renders a lesson's
 * Builder content (`learning-center-lesson-content` model). When `showFull` is
 * false (guest who hasn't visited this lesson) only a trimmed teaser is rendered
 * — the first text block, truncated — under a fade-out mask.
 *
 * `content` is the lesson's resolved Builder content object (`{ data: { blocks }}`);
 * its exact shape is asserted against the live `learning-center-lesson-content`
 * model during QA (Builder MCP was unavailable at build time — SFCC-11505 notes).
 */
interface LessonContentPreviewProps {
  content: unknown;
  builderModel: string;
  showFull: boolean;
}

const LessonContentPreview = ({
  content,
  builderModel,
  showFull,
}: LessonContentPreviewProps) => {
  const { config } = useBuilder();
  const apiKey = config.api_key;

  const entry = content as BuilderContent | undefined;
  const blocks =
    (entry?.data as { blocks?: unknown[] } | undefined)?.blocks ?? [];

  const previewContent = useMemo(() => {
    if (!entry) return entry;
    return {
      ...entry,
      data: { ...entry.data, blocks: getPreviewBlocks(blocks as never[], 1) },
    } as BuilderContent;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry]);

  if (!entry) return null;

  if (showFull) {
    return (
      <div data-testid="lessonContentPreview-full">
        <Content
          model={builderModel}
          content={entry}
          apiKey={apiKey}
          customComponents={customComponents}
          linkComponent={GuardedBuilderLink}
        />
      </div>
    );
  }

  return (
    <div
      className="relative max-h-[9rem] overflow-hidden"
      data-testid="lessonContentPreview-teaser"
    >
      <Content
        model={builderModel}
        content={previewContent}
        apiKey={apiKey}
        customComponents={customComponents}
        linkComponent={GuardedBuilderLink}
      />
      <div className="pointer-events-none absolute bottom-0 left-0 h-32 w-full bg-gradient-to-t from-white to-transparent" />
    </div>
  );
};

export default LessonContentPreview;
